import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cityService } from '../../services/cityService';
import { countryService } from '../../services/countryService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import CityModal from '../../components/Setup/CityModal';
import { PlusIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import DataGrid from '../../components/DataGrid';

const CitiesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryId, setCountryId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ status?: 'ACTIVE' | 'INACTIVE' }>({});
  const pageSize = 50;
  const queryClient = useQueryClient();

  const { data: countriesData } = useQuery({
    queryKey: ['countries', 'for-city-filter'],
    queryFn: () => countryService.getCountries({ page: 1, limit: 1000, sortBy: 'name', sortOrder: 'asc' }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['cities', currentPage, searchTerm, countryId, filters],
    queryFn: () => cityService.getCities({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      sortBy: 'name',
      sortOrder: 'asc',
      countryId: countryId || undefined,
      status: filters.status,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => cityService.deleteCity(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['cities'] }); setConfirmDelete(null); },
  });

  const handleExport = async () => {
    try {
      const blob = await cityService.exportCities({
        search: searchTerm,
        countryId: countryId || undefined,
        status: filters.status,
        ids: selectedIds.length ? selectedIds : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `cities-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    }
  };




  const toggleRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkAction = async (status: 'ACTIVE' | 'INACTIVE') => {
    if (!selectedIds.length) return;
    try {
      await cityService.bulkUpdateCityStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['cities'] });
    } catch (e) {
      console.error('Bulk update failed', e);
    }
  };

  const openCreate = () => { setSelected(null); setIsModalOpen(true); };
  const openEdit = useCallback((c: any) => { setSelected(c); setIsModalOpen(true); }, []);

  const cities = data?.cities || [];
  const pagination = data?.pagination;

  const countries = countriesData?.countries || [];

  const clearFilters = () => { setFilters({}); setSearchTerm(''); setCountryId(''); setCurrentPage(1); };

  const columns = useMemo<ColumnDef<any>[]>(() => [
    { id: 'name', accessorKey: 'name', header: 'City', meta: { sortable: true }, cell: (info) => info.getValue() as string },
    { id: 'country', header: 'Country', cell: ({ row }) => (row.original as any).country?.name || '-' },
    { id: 'status', header: 'Status', cell: ({ row }) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${((row.original as any).status === 'ACTIVE') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {(row.original as any).status}
      </span>
    ) },
    { id: 'actions', header: 'Actions', cell: ({ row }) => (
      <div className="flex items-center justify-end space-x-2">
        <button onClick={() => openEdit(row.original)} className="text-primary-600 hover:text-primary-900"><PencilSquareIcon className="w-4 h-4" /></button>
        <button onClick={() => setConfirmDelete(row.original)} className="text-red-600 hover:text-red-900"><TrashIcon className="w-4 h-4" /></button>
      </div>
    ) },
  ], [openEdit, setConfirmDelete]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Cities</h1>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bg-white rounded-lg shadow-soft p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">{selectedIds.length} selected</div>
          <div className="flex space-x-2">
            <Button onClick={() => handleBulkAction('ACTIVE')}>Activate</Button>
            <Button variant="danger" onClick={() => handleBulkAction('INACTIVE')}>Deactivate</Button>
            <Button variant="secondary" onClick={() => setSelectedIds([])}>Clear selection</Button>
          </div>
        </div>
      )}


      <div className="bg-white rounded-lg shadow-soft p-6 space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">

            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search cities..." className="pl-10 form-input w-full" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
          <div className="w-64">
            <select className="form-input w-full" value={countryId} onChange={(e) => { setCountryId(e.target.value); setCurrentPage(1); }}>
              <option value="">All Countries</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft">
        <div className="p-4">
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-input w-full"
                value={filters.status || ''}
                onChange={(e) => { setFilters({ status: (e.target.value as any) || undefined }); setCurrentPage(1); }}
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : (
            <DataGrid
              data={cities}
              columns={columns}
              getRowId={(r) => (r as any).id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) => setSelectedIds(checked ? ids : [])}
              sortBy={'name'}
              sortOrder={'asc'}
              onSortChange={() => {}}
            />
          )}
        </div>
        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
            </p>
            <div>
              <button className="btn btn-secondary rounded-r-none" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
              <button className="btn btn-secondary rounded-l-none" disabled={pagination && currentPage === pagination.pages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <CityModal
          city={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['cities'] }); }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete City"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default CitiesPage;

